import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ParentPhonePreview = ({ organizationName, heroImageUrl, supportingImageUrl, settings, lastActivityAt, firstChildName, firstChildPhoto, lastSignInTime, lastSignOutTime }) => {
  const visibility = settings?.parentPortalVisibility || {};
  const visibleCount = ["activityFeed","messages","documentUploads","enrollmentForms","invoicesAndPayments","centerAnnouncements","itemsNeeded","artworkAndPhotos","menus"]
    .filter((k) => visibility[k] !== false).length;

  // Format child name: "Last, First" → "First Last"
  const formatChildName = (name) => {
    if (!name) return "Phenix Mitchell";
    if (name.includes(",")) {
      const [last, first] = name.split(",").map(s => s.trim());
      return `${first} ${last}`;
    }
    return name;
  };

  const formatRelative = (iso) => {
    if (!iso) return "No updates yet";
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  return (
    <Card className="border-0 bg-[#0f172a] text-white shadow-2xl" data-testid="parent-phone-preview-card">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">Mobile preview</p>
          </div>
          <Badge className="bg-white/10 text-white text-xs" data-testid="parent-phone-preview-badge">{visibleCount} features visible</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mx-auto max-w-[330px] rounded-[2.5rem] bg-[#fff9f1] p-3 shadow-[0_30px_80px_rgba(15,23,42,0.55)]" data-testid="parent-phone-device-frame">
          <div className="rounded-[2rem] bg-white p-4 text-slate-900 relative">
            {/* Top-left child photo or placeholder */}
            <div className="absolute top-3 left-3">
              {firstChildPhoto ? (
                <img src={firstChildPhoto.startsWith('http') ? firstChildPhoto : `${BACKEND_URL}${firstChildPhoto}`} alt="child" className="h-16 w-16 rounded-full object-cover ring-2 ring-emerald-400" />
              ) : (
                <div className="h-16 w-16 rounded-full border-2 border-dashed border-emerald-400 bg-emerald-50 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="mb-3 flex flex-col items-center gap-1.5">
              <img src="/simple-sign-in-logo.svg" alt="logo" className="h-11 w-11 rounded-2xl ring-2 ring-emerald-400" data-testid="parent-phone-preview-logo" />
              <p className="text-lg font-bold text-center text-slate-900" data-testid="parent-phone-preview-child-name">{formatChildName(firstChildName)}</p>
              <p className="text-xs font-bold text-emerald-700 tracking-wide text-center uppercase">{organizationName || "Childcare Center"}</p>
            </div>

            <div
              className="relative overflow-hidden rounded-[1.5rem] h-44"
              style={{
                backgroundImage: (heroImageUrl || supportingImageUrl) ? `url(${heroImageUrl || supportingImageUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: !(heroImageUrl || supportingImageUrl) ? '#0f766e' : undefined,
              }}
              data-testid="parent-phone-preview-hero"
            >
              {/* Soft gradient — bottom only for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent rounded-[1.5rem]" />
              {/* Overlaid activity text */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-sm font-semibold text-white drop-shadow" data-testid="parent-phone-preview-last-update">
                  Last update: {formatRelative(lastActivityAt)}
                </p>
                <div className="flex gap-3 mt-1">
                  <div>
                    <p className="text-[10px] text-white/70">Signed In</p>
                    <p className="text-xs font-bold text-emerald-300">{lastSignInTime || '8:01 AM'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/70">Signed Out</p>
                    <p className="text-xs font-bold text-sky-300">{lastSignOutTime || '4:35 PM'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {settings.parentPortalVisibility.activityFeed !== false && (
                <div className="rounded-[1.4rem] bg-emerald-50 p-3" data-testid="parent-phone-preview-activity-card">
                  <p className="text-sm font-semibold text-emerald-700">Today's Activity</p>
                  <p className="mt-2 text-xs text-slate-600">Meals · naps · learning notes</p>
                </div>
              )}
              {settings.parentPortalVisibility.messages !== false && (
                <div className="rounded-[1.4rem] bg-violet-50 p-3" data-testid="parent-phone-preview-messages-card">
                  <p className="text-sm font-semibold text-violet-700">Messages</p>
                  <p className="mt-2 text-xs text-slate-600">Replies, center notes, parent threads</p>
                </div>
              )}
              {settings.parentPortalVisibility.documentUploads !== false && (
                <div className="rounded-[1.4rem] bg-sky-50 p-3" data-testid="parent-phone-preview-documents-card">
                  <p className="text-sm font-semibold text-sky-700">Enrollment Documents (PDF)</p>
                  <p className="mt-2 text-xs text-slate-600">Blank PDF forms to download, sign, and upload</p>
                </div>
              )}
              {settings.parentPortalVisibility.enrollmentForms !== false && (
                <div className="rounded-[1.4rem] bg-teal-50 p-3" data-testid="parent-phone-preview-forms-card">
                  <p className="text-sm font-semibold text-teal-700">Enrollment Forms (Custom Digital)</p>
                  <p className="mt-2 text-xs text-slate-600">Online forms to fill out and submit</p>
                </div>
              )}
              {settings.parentPortalVisibility.invoicesAndPayments !== false && (
                <div className="rounded-[1.4rem] bg-green-50 p-3" data-testid="parent-phone-preview-billing-card">
                  <p className="text-sm font-semibold text-green-700">Quick Pay</p>
                  <p className="mt-2 text-xs text-slate-600">Send payments instantly</p>
                </div>
              )}
              {settings.parentPortalVisibility.centerAnnouncements !== false && (
                <div className="rounded-[1.4rem] bg-rose-50 p-3" data-testid="parent-phone-preview-announce-card">
                  <p className="text-sm font-semibold text-rose-700">Announcements</p>
                  <p className="mt-2 text-xs text-slate-600">Center-wide updates & reminders</p>
                </div>
              )}
              {settings.parentPortalVisibility.itemsNeeded !== false && (
                <div className="rounded-[1.4rem] bg-cyan-50 p-3" data-testid="parent-phone-preview-items-card">
                  <p className="text-sm font-semibold text-cyan-700">Items needed</p>
                  <p className="mt-2 text-xs text-slate-600">Supplies, claim items, reminders</p>
                </div>
              )}
              {settings.parentPortalVisibility.artworkAndPhotos !== false && (
                <div className="rounded-[1.4rem] bg-orange-50 p-3" data-testid="parent-phone-preview-artwork-card">
                  <p className="text-sm font-semibold text-orange-700">Artwork & photos</p>
                  <p className="mt-2 text-xs text-slate-600">Classroom memories shared</p>
                </div>
              )}
              {settings.parentPortalVisibility.menus !== false && (
                <div className="rounded-[1.4rem] bg-yellow-50 p-3" data-testid="parent-phone-preview-menus-card">
                  <p className="text-sm font-semibold text-yellow-700">Menus</p>
                  <p className="mt-2 text-xs text-slate-600">Monthly meal menus from your center</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
